import { useEffect, useMemo, useState } from "react";
import { http } from "../services/http";
import { Link } from "react-router-dom";

type ProfileApi = {
  user: {
    userId: number;
    username: string;
    email: string;
    phone: string | null;
    pointAccumulated: number | null;
    pointUsed: number | null;
    createdAt: string;
  };
  pointValue: number;
  availablePoints: number;
  pointToMoneyRate: number;
  vouchers: any[];
  printedVouchers: any[];
};

type OrderApi = {
  orderId: number;
  userId: number;
  orderDate: string;
  totalPrice: number;
  subtotal: number;
  statusId: number;
  voucherCode: string | null;
  discountAmount: number;
  shippingFee: number;
  loyaltyPointDiscount: number;
  receiverName: string;
  phone: string;
  email: string;
  address: string;
  paymentMethod: string;
  paymentStatus: string;
  shippingStatus: string;
  orderDetails: Array<{
    productName: string;
    imageUrl: string;
    quantity: number;
    price: number;
    size: string;
    color: string;
    subtotal: number;
  }>;
};

const formatVND = (n: number) => n.toLocaleString("vi-VN") + "₫";

const statusLabel = (o: OrderApi) => {
  if (o.statusId === 11) return "Chờ xác nhận";
  if (o.statusId === 10) return "Đã xác nhận";
  if (o.paymentStatus) return o.paymentStatus;
  if (o.shippingStatus) return o.shippingStatus;
  return `Status #${o.statusId}`;
};

const statusBadgeCls = (o: OrderApi) => {
  if (o.statusId === 11) return "bg-amber-50 text-amber-700 ring-amber-200";
  if (o.statusId === 10) return "bg-emerald-50 text-emerald-700 ring-emerald-200";
  return "bg-neutral-50 text-neutral-600 ring-neutral-200";
};

const initials = (s: string) => {
  const t = s.trim();
  if (!t) return "OD";
  const parts = t.split(/\s+/);
  const a = parts[0]?.[0] || "";
  const b = parts[parts.length - 1]?.[0] || "";
  return (a + b).toUpperCase();
};

function Card({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return (
    <section
      className={[
        "rounded-3xl border border-black/5 bg-white/70 backdrop-blur",
        "shadow-[0_10px_40px_-20px_rgba(0,0,0,0.35)]",
        className,
      ].join(" ")}
    >
      {children}
    </section>
  );
}

export default function ProfilePage() {
  const [profile, setProfile] = useState<ProfileApi | null>(null);
  const [orders, setOrders] = useState<OrderApi[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let mounted = true;

    Promise.all([http.get("/api/Users/profile"), http.get("/api/Order/user-orders")])
      .then(([p, o]) => {
        if (!mounted) return;
        setProfile(p.data);
        setOrders(o.data);
      })
      .catch(() => setError("Không thể tải thông tin tài khoản"))
      .finally(() => mounted && setLoading(false));

    return () => {
      mounted = false;
    };
  }, []);

  const user = profile?.user;

  const displayName = useMemo(() => user?.username || "OWL DREAM Member", [user?.username]);

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-16 text-center text-sm text-neutral-500">
        Đang tải thông tin tài khoản...
      </div>
    );
  }

  if (error || !profile || !user) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-16 text-center text-sm text-red-600">
        {error || "Không có dữ liệu"}
      </div>
    );
  }

  return (
    <div className="min-h-[70vh] bg-[#f6f3ee]">
      <div className="max-w-6xl mx-auto px-4 pt-14 pb-10">
        <p className="text-[11px] uppercase tracking-[0.45em] text-neutral-600">
          OWL DREAM — MY ACCOUNT
        </p>

        <div className="mt-3 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
          <div>
            <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-neutral-900">
              Tài khoản của tôi
            </h1>
            <p className="mt-2 text-sm text-neutral-600 max-w-xl">
              Quản lý thông tin cá nhân và theo dõi đơn hàng.
            </p>
          </div>

          <div className="flex gap-2">
            <Link
              to="/profile/edit"
              className="inline-flex items-center justify-center rounded-2xl px-4 py-2.5 text-sm font-medium border border-black/10 bg-white hover:bg-black hover:text-white transition"
            >
              Cập nhật thông tin
            </Link>
            <Link
              to="/shop"
              className="inline-flex items-center justify-center rounded-2xl px-4 py-2.5 text-sm font-medium bg-black text-white hover:opacity-90 transition"
            >
              Tiếp tục mua sắm
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 pb-16">
        <div className="grid lg:grid-cols-3 gap-6">
          {/* PROFILE */}
          <Card className="p-6 lg:col-span-1">
            <div className="flex items-center gap-4">
              <div className="h-14 w-14 rounded-2xl bg-black text-white flex items-center justify-center font-semibold">
                {initials(displayName)}
              </div>

              <div className="min-w-0">
                <p className="text-lg font-semibold text-neutral-900 truncate">{displayName}</p>
                <p className="text-sm text-neutral-600 truncate">{user.email}</p>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-2 gap-3">
              <div className="rounded-2xl border border-black/5 bg-white p-4">
                <p className="text-xs text-neutral-500">Số điện thoại</p>
                <p className="mt-1 text-sm font-medium text-neutral-900">{user.phone || "—"}</p>
              </div>

              <div className="rounded-2xl border border-black/5 bg-white p-4">
                <p className="text-xs text-neutral-500">Điểm khả dụng</p>
                <p className="mt-1 text-sm font-semibold text-neutral-900">
                  {profile.availablePoints}
                  <span className="text-neutral-500 font-medium">
                    {" "}
                    (~ {formatVND(profile.pointValue)})
                  </span>
                </p>
              </div>

              <div className="rounded-2xl border border-black/5 bg-white p-4 col-span-2">
                <p className="text-xs text-neutral-500">Tỷ lệ quy đổi</p>
                <p className="mt-1 text-sm font-medium text-neutral-900">
                  1 điểm = {formatVND(profile.pointToMoneyRate)}
                </p>
              </div>

              <div className="rounded-2xl border border-black/5 bg-white p-4 col-span-2">
                <p className="text-xs text-neutral-500">Điểm tích luỹ / đã dùng</p>
                <p className="mt-1 text-sm font-medium text-neutral-900">
                  {(user.pointAccumulated ?? 0).toLocaleString("vi-VN")} /{" "}
                  {(user.pointUsed ?? 0).toLocaleString("vi-VN")}
                </p>
              </div>
            </div>

            <div className="mt-6 flex gap-2">
              <Link
                to="/profile/edit"
                className="flex-1 rounded-2xl px-4 py-2.5 text-sm font-medium border border-black/10 bg-white hover:bg-black hover:text-white transition text-center"
              >
                Chỉnh sửa
              </Link>
              <Link
                to="/address"
                className="flex-1 rounded-2xl px-4 py-2.5 text-sm font-medium border border-black/10 bg-white hover:bg-black hover:text-white transition text-center"
              >
                Địa chỉ
              </Link>
            </div>
          </Card>

          {/* ORDERS */}
          <Card className="p-6 lg:col-span-2">
            <div className="flex items-center justify-between gap-4">
              <div>
                <h2 className="text-lg font-semibold text-neutral-900">Đơn hàng của tôi</h2>
                <p className="text-sm text-neutral-600 mt-1">
                  Xem trạng thái, thanh toán và sản phẩm trong đơn.
                </p>
              </div>
              <div className="text-sm text-neutral-600">{orders.length} đơn</div>
            </div>

            {orders.length === 0 ? (
              <div className="mt-6 rounded-3xl border border-dashed border-black/15 bg-white p-10 text-center">
                <p className="text-sm text-neutral-700 font-medium">Bạn chưa có đơn hàng nào</p>
              </div>
            ) : (
              <div className="mt-6 space-y-3">
                {orders.map((o) => {
                  const first = o.orderDetails?.[0];
                  return (
                    <Link
                      key={o.orderId}
                      to={`/orders/${o.orderId}`}
                      className="group block rounded-3xl border border-black/5 bg-white p-5 hover:border-black/15 hover:shadow-[0_14px_50px_-30px_rgba(0,0,0,0.55)] transition"
                    >
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div className="flex items-center gap-4 min-w-0">
                          {first?.imageUrl ? (
                            <img
                              src={first.imageUrl}
                              alt={first.productName}
                              className="h-14 w-14 rounded-2xl object-cover ring-1 ring-black/10"
                            />
                          ) : (
                            <div className="h-14 w-14 rounded-2xl bg-black/5" />
                          )}

                          <div className="min-w-0">
                            <div className="flex items-center gap-3">
                              <p className="text-base font-semibold text-neutral-900 truncate">
                                #{o.orderId}
                              </p>
                              <span
                                className={[
                                  "inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset",
                                  statusBadgeCls(o),
                                ].join(" ")}
                              >
                                {statusLabel(o)}
                              </span>
                            </div>

                            <p className="mt-1 text-sm text-neutral-600 truncate">
                              {new Date(o.orderDate).toLocaleDateString("vi-VN")}
                              <span className="mx-2 text-neutral-300">•</span>
                              {o.paymentMethod?.toUpperCase()} — {o.paymentStatus}
                            </p>

                            {first && (
                              <p className="mt-1 text-sm text-neutral-700 truncate">
                                {first.productName} • {first.color} • Size {first.size} • x{first.quantity}
                              </p>
                            )}
                          </div>
                        </div>

                        <div className="flex items-end sm:items-center justify-between sm:justify-end gap-6">
                          <div className="text-right">
                            <p className="text-sm text-neutral-500">Tổng</p>
                            <p className="text-lg font-semibold text-neutral-900">
                              {formatVND(o.totalPrice)}
                            </p>
                            {(o.shippingFee ?? 0) > 0 && (
                              <p className="text-xs text-neutral-500">
                                Ship: {formatVND(o.shippingFee)}
                              </p>
                            )}
                          </div>

                          <span className="text-sm font-medium text-neutral-900 group-hover:translate-x-0.5 transition">
                            Xem chi tiết →
                          </span>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
